import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { parseParamToBooleanRecord, serializeBooleanRecordToParam } from 'v2/filters/filterUrlParams';

type FilterParamPatch<ParamKey extends string> = Partial<Record<ParamKey, string | undefined>>;

export const useFilterSearchParams = <ParamKey extends string>(paramKeys: ParamKey[]) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Takes a computePatch function that receives the current URLSearchParams and returns a partial map of
  // { paramKey: value | undefined }. It applies that patch: set when a value is given, delete when undefined,
  // then calls setSearchParams with { replace: true } (so filter changes don't spam browser history).
  const updateFilterParams = useCallback(
    (computePatch: (current: URLSearchParams) => FilterParamPatch<ParamKey>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          const patch = computePatch(current);

          paramKeys.forEach((key) => {
            if (!(key in patch)) return;

            const value = patch[key];
            if (value === undefined) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, paramKeys]
  );

  // Updates a single param that stores a multi-select boolean filter (e.g. { admin: true, user: false }),
  // serialized as a comma-separated list of the selected keys.
  const updateMultiSelectFilter = useCallback(
    <K extends string>(
      paramKey: ParamKey,
      defaults: Record<K, boolean>,
      value: Record<K, boolean> | ((current: Record<K, boolean>) => Record<K, boolean>)
    ) => {
      updateFilterParams((current) => {
        const currentFilter = parseParamToBooleanRecord(current.get(paramKey), defaults);

        const nextFilter = typeof value === 'function' ? value(currentFilter) : value;

        const patch: FilterParamPatch<ParamKey> = {};
        patch[paramKey] = serializeBooleanRecordToParam(nextFilter);

        return patch;
      });
    },
    [updateFilterParams]
  );

  return { searchParams, updateFilterParams, updateMultiSelectFilter };
};
